import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableCell as MuiTableCell,
    Button
} from '@mui/material';
import { styled, tableCellClasses, Chip } from '@mui/material';
import { PrimaryButton } from '../components';
import InfoHeader from '../components/InfoHeader';

import { useAuth } from '../context/AuthContext';
import { colaboradorService } from '../services/ColaboradorService';
import toastService from '../utils/toastService';
import Colaborador from '../types/negocio/Colaborador';
import ProtecaoAcesso from '../components/ProtecaoAcesso';
import ProtecaoBotao from '../components/ProtecaoBotao';
import ProtectedBasicMenu from '../components/ProtectedBasicMenu';
import ColaboradorModal from '../components/modais/ColaboradorModal';
import { departamentoService } from '../services/DepartamentoService';
import { Departamento } from '../types/negocio/Departamento';
import LoadingIndicator from '../components/LoadingIndicator';

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#042E4B',
        color: theme.palette.common.white,
        fontWeight: 'bold'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

export default function Colaboradores() {
    const authContext = useAuth();
    const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
    const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalAberto, setModalAberto] = useState(false);
    const [modoEdicao, setModoEdicao] = useState(false);
    const [colaboradorSelecionado, setColaboradorSelecionado] = useState<Colaborador>({} as Colaborador);


    console.log('Colaboradores: Componente renderizado, user:', authContext.user);

    useEffect(() => {
        fetchColaboradores();
    }, []);

    const criarColaboradorVazio = (): Colaborador => ({
        nomeCompleto: '',
        cargoAtual: '',
        departamento: '',
        telefone: '',
        celular: '',
        email: '',
        admissao: new Date().toISOString(),
        formacaoAcademica: '',
        certificacoes: '',
        experienciaProfissional: '',
        treinamentosRealizados: '',
        habilidadesTecnicas: '',
        habilidadesComportamentais: '',
        idiomas: '',
        senha: '',
        ativo: true,
        perfilGuid: undefined,
        perfil: undefined
    });

    const fetchColaboradores = async () => {
        try {
            console.log('Colaboradores: Iniciando carregamento de dados...');
            setIsLoading(true);
            setError(null);

            // Verificar se o usuário ainda está autenticado
            if (!authContext.user) {
                console.log('Colaboradores: Usuário não autenticado durante fetch');
                setError("Usuário não autenticado.");
                return;
            }
            const colaboradoresData = await colaboradorService.listarTodos();
            const departamentosData = await departamentoService.listarTodos();

            console.log('Colaboradores: Dados carregados com sucesso:', { colaboradoresData, departamentosData });
            setColaboradores(colaboradoresData);
            setDepartamentos(departamentosData);        
        } catch (err: any) {
        console.error("Erro ao carregar colaboradores:", err);

        // Não mostrar erro se for 401, pois o usuário será redirecionado
        if (err.response?.status !== 401) {
            const errorMessage = err.response?.data?.message || err.message || "Erro desconhecido";
            setError(`Não foi possível carregar os dados dos colaboradores: ${errorMessage}`);
            toastService.error("Erro ao carregar dados dos colaboradores.");
        }
    } finally {
        setIsLoading(false);
    }
};


const handleEdit = (index: number) => {
    setColaboradorSelecionado(colaboradores[index]);
    setModoEdicao(true);
    setModalAberto(true);
};

const handleNovoColaborador = () => {
    setColaboradorSelecionado(criarColaboradorVazio());
    setModoEdicao(true);
    setModalAberto(true);
};

const handleViewMore = (index: number) => {
    setColaboradorSelecionado(colaboradores[index]);
    setModoEdicao(false);
    setModalAberto(true);
};

const handleSaveColaborador = async (data: Colaborador) => {
    try {
        // Verifica se é um novo colaborador (sem GUID ou GUID vazio) ou edição
        const isNovoColaborador = !data.guid || data.guid.trim() === '';

        await colaboradorService.save(data);

        toastService.success(isNovoColaborador ? 'Colaborador criado com sucesso!' : 'Colaborador atualizado com sucesso!');
        setModalAberto(false);

        // atualizar a lista
        const listaAtualizada = await colaboradorService.listarTodos();
        setColaboradores(listaAtualizada);
    } catch (error) {
        toastService.error('Erro ao salvar colaborador.');
    }
};


const handleDelete = async (index: number) => {
    const colaboradorToDelete = colaboradores[index];

    if (!colaboradorToDelete?.guid) {
        toastService.error("Colaborador não encontrado.");
        return;
    }

    try {
        await colaboradorService.deletar(colaboradorToDelete.guid);
        const novaLista = colaboradores.filter((_, i) => i !== index);
        setColaboradores(novaLista);
        toastService.success("Colaborador excluído com sucesso!");
    } catch (error) {
        console.error("Erro ao excluir colaborador:", error);
        toastService.error("Erro ao excluir colaborador. Tente novamente.");
    }
};

console.log('Colaboradores: Renderizando componente principal, isLoading:', isLoading, 'error:', error);

// Show loading state when data is being fetched
if (isLoading) {
    return <LoadingIndicator fullHeight message="Carregando colaboradores..." />;
}

// Show error state only after loading is complete and there's an error
if (error && !isLoading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
            <Typography color="error">{error}</Typography>
            <PrimaryButton onClick={() => globalThis.location.reload()}>
                Tentar Novamente
            </PrimaryButton>
        </Box>
    );
}

return (
    <ProtecaoAcesso tela="colaboradores">
        <Box sx={{ p: 2 }}>
            <InfoHeader
                title="Colaboradores"
                message="Gerencie os colaboradores que farão parte do planejamento estratégico da sua empresa."
                isImportant
                url="https://youtu.be/J8EYnc7UeQk"
                modalTitle="Entenda a aba Colaboradores"
            />
                
            <ProtecaoBotao tela="colaboradores" tipo="alteracao">
                <PrimaryButton variant="contained" onClick={handleNovoColaborador}>
                    + Adicionar
                </PrimaryButton>
            </ProtecaoBotao>

            <Paper elevation={3} sx={{ mt: 2 }}>
                <TableContainer sx={{ borderRadius: '8px' }}>
                    <Table aria-label="colaboradores table" sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell><strong>Nome completo</strong></StyledTableCell>
                                <StyledTableCell><strong>Email</strong></StyledTableCell>
                                <StyledTableCell><strong>Cargo atual</strong></StyledTableCell>
                                <StyledTableCell><strong>Departamento</strong></StyledTableCell>
                                <StyledTableCell align="center"><strong>Ações</strong></StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {colaboradores.map((colab, index) => (
                                <StyledTableRow key={colab.guid || index}>
                                    <StyledTableCell
                                        component="th"
                                        scope="row"
                                        onClick={() => !colab.owner && handleViewMore(index)}
                                        sx={{ cursor: colab.owner ? "default" : "pointer" }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontSize: 16, "&:hover": colab.owner ? {} : { textDecoration: "underline", color: "primary.main" } }}>
                                                {colab.nomeCompleto}
                                            </Typography>
                                            {colab.owner && (
                                                <Chip label="Proprietário" size="small" color="primary" variant="outlined" />
                                            )}
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell component="th" scope="row">
                                        <Typography variant="body2" sx={{ fontSize: 16 }}>
                                            {colab.email}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell component="th" scope="row">
                                        <Typography variant="body2" sx={{ fontSize: 16 }}>
                                            {colab.cargoAtual}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell component="th" scope="row">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: 16,
                                                color: colab.departamento && departamentos.find(dep => dep.guid === colab.departamento)
                                                    ? 'text.primary'
                                                    : 'secondary.main'
                                            }}
                                        >
                                            {colab.owner ? '—' : (departamentos.find(dep => dep.guid === colab.departamento)?.nome || "Sem departamento")}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        {colab.owner ? (
                                            <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                                                —
                                            </Typography>
                                        ) : (
                                            <ProtectedBasicMenu
                                                tela="colaboradores"
                                                onEdit={() => handleEdit(index)}
                                                onDelete={() => handleDelete(index)}
                                                onViewMore={() => handleViewMore(index)}
                                                deleteItemName={colab.nomeCompleto}
                                            />
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <ColaboradorModal
                open={modalAberto}
                onClose={() => setModalAberto(false)}
                colaborador={colaboradorSelecionado}
                departamentos={departamentos}
                modoEdicao={modoEdicao}
                setColaborador={modoEdicao ? setColaboradorSelecionado : undefined}
                onSave={modoEdicao ? handleSaveColaborador : undefined}
                onEditClick={() => setModoEdicao(true)}
                isNovoColaborador={!colaboradorSelecionado?.guid || colaboradorSelecionado.guid.trim() === ''}
            />
        </Box>
    </ProtecaoAcesso>
);
}
